import { ObendyRoute } from '@core/app-routing';
import { canAccessServiceFeature } from '@core/guards/can-access-service-features.guard';
import { canViewProgressGuard } from '@core/guards/wizbii-drive/can-view-progress.guard';
import { updateServiceConfigurationMiddleware } from '@core/middlewares/update-service-configuration.middleware';
import { serviceTabTitleResolver } from '@core/title-strategy/service-tab-title.resolver';
import { serviceTitleResolver } from '@core/title-strategy/service-title.resolver';
import { strapiBlogAdvicesResolver } from '@domains/services/blog/resolvers/article/blog-article.resolver';
import { environment } from '@environment';
import { DriveRoutingEnum } from '@features/services/drive/drive-routing.enum';
import { serieResumeResolver } from '@features/services/drive/drive-serie-resume/resolvers/serie-resume.resolver';
import { lessonResolver } from '@features/services/drive/resolvers/lesson/lesson.resolver';
import { serieResolver } from '@features/services/drive/resolvers/serie/serie.resolver';
import { ServiceWithManyTabs } from '@features/services/external-service/guards/service-with-many-tabs.guard';
import { TabContentFromSlugResolver } from '@features/services/external-service/resolvers/tab-content-from-slug.resolver';
import { ServicesRoutingEnum } from '@features/services/services-routing.enum';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import * as driveEffects from '@stores/drive/drive.effects';
import { driveFeatureKey, driveReducer } from '@stores/drive/drive.reducer';
import { INTERNAL_SERVICES_IDS } from '@stores/services/model';

export const WIZBII_DRIVE_ROUTE: ObendyRoute = {
  path: ServicesRoutingEnum.Drive,
  title: serviceTitleResolver,
  showInSiteMap: false,
  canActivate: [updateServiceConfigurationMiddleware],
  data: {
    serviceId: INTERNAL_SERVICES_IDS.drive,
  },

  loadChildren: () => [
    {
      path: '',
      showInSiteMap: false,
      providers: [provideState({ name: driveFeatureKey, reducer: driveReducer }), provideEffects(driveEffects)],

      children: [
        {
          path: `${DriveRoutingEnum.Play}/${DriveRoutingEnum.Serie}`,
          showInSiteMap: false,
          data: {
            showHeader: false,
            showFooter: false,
            titlePath: 'serie.name',
          },
          canActivate: [canAccessServiceFeature({ redirectWhenConsentsAccepted: true })],
          resolve: {
            serie: serieResolver,
          },
          loadComponent: () =>
            import('@features/services/drive/drive-serie-play/drive-serie-play.component').then(
              (cmp) => cmp.DriveSeriePlayComponent
            ),
        },

        {
          path: `${DriveRoutingEnum.Resume}/${DriveRoutingEnum.Serie}`,
          showInSiteMap: false,
          data: {
            showHeader: false,
            showFooter: false,
            titlePath: 'serieResume.serie.name',
          },
          canActivate: [canAccessServiceFeature({ redirectWhenConsentsAccepted: true })],
          resolve: {
            serieResume: serieResumeResolver,
          },
          loadComponent: () =>
            import('@features/services/drive/drive-serie-resume/drive-serie-resume.component').then(
              (cmp) => cmp.DriveSerieResumeComponent
            ),
        },

        {
          path: DriveRoutingEnum.ResultSerieId,
          showInSiteMap: false,
          loadComponent: () =>
            import('@features/services/drive/drive-result/drive-result.component').then(
              (cmp) => cmp.DriveResultComponent
            ),
        },

        {
          path: '',
          showInSiteMap: false,
          loadComponent: () =>
            import('@features/services/drive/drive-layout/drive-layout.component').then(
              (cmp) => cmp.DriveLayoutComponent
            ),

          loadChildren: () => [
            {
              path: DriveRoutingEnum.Lessons,
              title: 'Cours',
              showInSiteMap: false,

              loadChildren: () => [
                {
                  path: '',
                  showInSiteMap: false,
                  loadComponent: () =>
                    import('@features/services/drive/drive-lesson-list/drive-lesson-list.component').then(
                      (cmp) => cmp.DriveLessonListComponent
                    ),
                },
              ],
            },

            {
              path: DriveRoutingEnum.Series,
              showInSiteMap: false,
              loadComponent: () =>
                import('@features/services/drive/drive-series/drive-series.component').then(
                  (cmp) => cmp.DriveSeriesComponent
                ),

              loadChildren: () => [
                {
                  path: '',
                  title: 'Séries',
                  showInSiteMap: false,
                  loadComponent: () =>
                    import(
                      '@features/services/drive/drive-series/drive-series-all-list/drive-series-all-list.component'
                    ).then((cmp) => cmp.DriveSeriesAllListComponent),
                },

                {
                  path: DriveRoutingEnum.ThemeThemeId,
                  title: 'Séries',
                  showInSiteMap: false,
                  loadComponent: () =>
                    import(
                      '@features/services/drive/drive-series/drive-series-theme-list/drive-series-theme-list.component'
                    ).then((cmp) => cmp.DriveSeriesThemeListComponent),
                },
              ],
            },

            {
              path: DriveRoutingEnum.Progress,
              showInSiteMap: false,
              title: 'Progression',
              loadComponent: () =>
                import('@features/services/drive/drive-progress/drive-progress.component').then(
                  (cmp) => cmp.DriveProgressComponent
                ),
              canActivate: [canViewProgressGuard],
            },

            {
              path: DriveRoutingEnum.Advices,
              showInSiteMap: false,
              title: 'Conseils',

              loadChildren: () => [
                {
                  path: '',
                  loadComponent: () =>
                    import('@features/services/drive/drive-advice-list/drive-advice-list.component').then(
                      (cmp) => cmp.DriveAdviceListComponent
                    ),
                  showInSiteMap: false,
                },
              ],
            },

            {
              path: '',
              showInSiteMap: false,
              redirectTo: DriveRoutingEnum.Lessons,
              pathMatch: 'full',
            },
          ],
        },

        {
          path: DriveRoutingEnum.Advices,
          showInSiteMap: false,
          title: 'Conseils',
          loadChildren: () => [
            {
              path: DriveRoutingEnum.AdviceSlug,
              resolve: {
                advice: strapiBlogAdvicesResolver(environment.strapi.driveApi),
              },
              data: {
                titlePath: 'advice.title',
                algoliaArticleIndex: environment.algolia.index.wizbiiDriveAdvices,
              },
              loadComponent: () =>
                import('@features/services/drive/drive-advice/drive-advice.component').then(
                  (cmp) => cmp.DriveAdviceComponent
                ),
              showInSiteMap: false,
            },
          ],
        },

        {
          path: DriveRoutingEnum.Lessons,
          title: 'Cours',
          showInSiteMap: false,
          loadChildren: () => [
            {
              path: `${DriveRoutingEnum.LessonId}/${DriveRoutingEnum.Chapter}/${DriveRoutingEnum.ChapterNumber}`,
              loadComponent: () =>
                import('@features/services/drive/drive-lesson/drive-lesson.component').then(
                  (cmp) => cmp.DriveLessonComponent
                ),
              resolve: {
                lesson: lessonResolver,
              },
              data: {
                titlePath: 'lesson.title',
              },
              showInSiteMap: false,
            },
            {
              path: `${DriveRoutingEnum.LessonId}`,
              showInSiteMap: false,
              redirectTo: `${DriveRoutingEnum.LessonId}/${DriveRoutingEnum.Chapter}/1`,
            },
          ],
        },

        {
          path: DriveRoutingEnum.TabSlug,
          title: serviceTabTitleResolver,
          showInSiteMap: false,
          canActivate: [ServiceWithManyTabs],
          resolve: {
            content: TabContentFromSlugResolver,
          },
          loadComponent: async () =>
            (await import('@features/services/service-with-rich-content/service-with-rich-content.component'))
              .ServiceWithRichContentComponent,
        },
      ],
    },
  ],
};
